/**
 * The one sheet behaviour everything shares. Anything bigger than a few buttons
 * is a sheet: it slides up from the bottom, sits over the chart while in use,
 * and clears away after. The mode toolset, an object's attributes, a tidal
 * graph and a scenario question all use this, so the app feels the same
 * everywhere and nothing advanced becomes permanent furniture.
 */
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useShellStore, type SheetName } from '@/store/shell';

/**
 * Sheets opened FROM another sheet return to that parent on dismiss, rather
 * than closing all the way to the chart, so the user lands back where they
 * were. The leg-XTD and route-alarms editors are reached from the route info,
 * so backing out of them returns there.
 */
const PARENT_SHEET: Partial<Record<SheetName, SheetName>> = {
  legxtd: 'route',
  routealarms: 'route',
};

export function Backdrop() {
  const activeSheet = useShellStore((s) => s.activeSheet);
  const closeSheet = useShellStore((s) => s.closeSheet);
  const openSheet = useShellStore((s) => s.openSheet);
  // The leg-XTD editor needs the chart reachable to drag the corridor handles,
  // so its backdrop doesn't dim or capture touches (the sheet's own buttons
  // close it instead of a tap-outside).
  const passThrough = activeSheet === 'legxtd';
  const onDismiss = () => {
    const parent = activeSheet ? PARENT_SHEET[activeSheet] : undefined;
    if (parent) openSheet(parent);
    else closeSheet();
  };
  return (
    <div
      className={`acp-backdrop${activeSheet && !passThrough ? ' show' : ''}`}
      onClick={onDismiss}
      aria-hidden={!activeSheet}
    />
  );
}

export interface SheetProps {
  name: SheetName;
  title: string;
  /** Optional element after the title, e.g. a "simulated" pill. */
  titleTag?: ReactNode;
  sub?: ReactNode;
  children?: ReactNode;
}

export function Sheet({ name, title, titleTag, sub, children }: SheetProps) {
  const activeSheet = useShellStore((s) => s.activeSheet);
  const open = activeSheet === name;
  return (
    <div
      className={`acp-sheet${open ? ' show' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-hidden={!open}
      aria-label={title}
    >
      <div className="acp-grab" />
      <h2>
        {title}
        {titleTag}
      </h2>
      {sub && <p className="sub">{sub}</p>}
      {children}
    </div>
  );
}

/** The transient toast, driven from the shell store. */
export function Toast() {
  const toast = useShellStore((s) => s.toast);
  const showToast = useShellStore((s) => s.showToast);
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => showToast(null), 1900);
    return () => window.clearTimeout(t);
  }, [toast, showToast]);
  return <div className={`acp-toast${toast ? ' show' : ''}`}>{toast}</div>;
}
